import React, { useCallback } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { ChevronLeft, ChevronRight, Sparkles, Shield, Zap, Layout, Video, Utensils, Baby, FileText, Stethoscope } from 'lucide-react';
import { Button } from './ui/button';

// Import the user-provided screens
import screenCalendar from '../assets/screens/screen-calendar.jpg';
import screenAiChat from '../assets/screens/screen-ai-chat.jpg';
import screenPregnancy from '../assets/screens/screen-pregnancy.jpg';
import screenDashboard from '../assets/screens/screen-dashboard.png';
import screenVideos from '../assets/screens/screen-videos.png';
import screenDietPlan from '../assets/screens/screen-diet-plan.png';
import screenPregnancyWeek from '../assets/screens/screen-pregnancy-week.png';
import screenPostnatal from '../assets/screens/screen-postnatal.png';
import screenDoctorReport from '../assets/screens/screen-doctor-report.png';

const FeaturesCarousel = () => {
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { 
      loop: true,
      align: 'center',
      skipSnaps: false,
    },
    [Autoplay({ delay: 5000, stopOnInteraction: false })]
  );

  const onSelect = React.useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  React.useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
  }, [emblaApi, onSelect]);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const appScreens = [
    {
      image: screenCalendar,
      title: "Smart Cycle Tracking",
      subtitle: "Calendar & Predictions",
      description: "Get pinpoint accuracy for your next period, ovulation, and fertile window with our AI-driven calendar.",
      icon: Sparkles,
      color: "from-pink-500/20 to-rose-500/20",
      accent: "text-pink-600"
    },
    {
      image: screenAiChat,
      title: "AI Health Assistant",
      subtitle: "24/7 Virtual Support",
      description: "Ask anything about your symptoms or fertility. Our AI assistant provides personalized, evidence-based guidance.",
      icon: Zap,
      color: "from-purple-500/20 to-indigo-500/20",
      accent: "text-purple-600"
    },
    {
      image: screenDashboard,
      title: "All-in-One Health Hub",
      subtitle: "Quick Action Center",
      description: "Easily log periods, symptoms, and sleep, or access the Ovalu store and community forums from one screen.",
      icon: Layout,
      color: "from-orange-500/20 to-amber-500/20",
      accent: "text-orange-600"
    },
    {
      image: screenPregnancy,
      title: "Pregnancy Mode",
      subtitle: "Journey Companion",
      description: "Track your baby's development week by week with tailored health tips, diet plans, and daily reminders.",
      icon: Shield,
      color: "from-blue-500/20 to-cyan-500/20",
      accent: "text-blue-600"
    },
    {
      image: screenPregnancyWeek,
      title: "Pregnancy Milestones",
      subtitle: "Visual Growth Tracking",
      description: "Watch your baby grow with size comparisons and weekly development milestones tailored to your journey.",
      icon: Baby,
      color: "from-green-500/20 to-emerald-500/20",
      accent: "text-green-600"
    },
    {
      image: screenVideos,
      title: "Educational Content",
      subtitle: "Weekly Growth Videos",
      description: "Access expert-led videos documenting your baby's development from week one through birth.",
      icon: Video,
      color: "from-red-500/20 to-rose-500/20",
      accent: "text-red-600"
    },
    {
      image: screenDietPlan,
      title: "Optimized Nutrition",
      subtitle: "Daily Meal Plans",
      description: "Fuel your body and your baby with optimized diet plans and key nutrient tracking for every trimester.",
      icon: Utensils,
      color: "from-yellow-500/20 to-orange-500/20",
      accent: "text-yellow-600"
    },
    {
      image: screenPostnatal,
      title: "Postnatal Support",
      subtitle: "Baby & Recovery Tracker",
      description: "Transition to motherhood with specialized tracking for recovery, feeding schedules, and baby milestones.",
      icon: Stethoscope,
      color: "from-violet-500/20 to-purple-500/20",
      accent: "text-violet-600"
    },
    {
      image: screenDoctorReport,
      title: "Doctor-Ready Reports",
      subtitle: "AI Health Summaries",
      description: "Generate comprehensive health reports based on your data to share with your medical provider during checkups.",
      icon: FileText,
      color: "from-teal-500/20 to-emerald-500/20",
      accent: "text-teal-600"
    }
  ];

  return (
    <div className="relative py-12 px-4 max-w-6xl mx-auto">
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex">
          {appScreens.map((screen, index) => (
            <div key={index} className="flex-[0_0_100%] min-w-0 px-4">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                {/* Phone Mockup side */}
                <div className="order-2 lg:order-1 flex justify-center">
                  <div className="phone-mockup group-hover:scale-105 transition-transform duration-500 shadow-2xl ring-1 ring-pink-100">
                    <img src={screen.image} alt={screen.title} />
                  </div>
                </div>

                {/* Text Content side */}
                <div className="order-1 lg:order-2 space-y-6 text-center lg:text-left">
                  <div className={`inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r ${screen.color} ${screen.accent} font-semibold text-sm`}>
                    <screen.icon className="w-4 h-4 mr-2" />
                    {screen.subtitle}
                  </div>
                  <h3 className="text-4xl font-bold text-gray-900 leading-tight">
                    {screen.title}
                  </h3>
                  <p className="text-xl text-gray-600 leading-relaxed max-w-xl">
                    {screen.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modern Navigation Controls */}
      <div className="absolute top-1/2 -translate-y-1/2 left-0 right-0 flex justify-between pointer-events-none px-2 lg:-mx-12">
        <Button
          onClick={scrollPrev}
          variant="ghost"
          className="pointer-events-auto rounded-full w-14 h-14 bg-white/80 backdrop-blur-sm border border-pink-100 text-[#FF6B9D] hover:bg-white shadow-lg transition-all"
        >
          <ChevronLeft className="w-8 h-8" />
        </Button>
        <Button
          onClick={scrollNext}
          variant="ghost"
          className="pointer-events-auto rounded-full w-14 h-14 bg-white/80 backdrop-blur-sm border border-pink-100 text-[#FF6B9D] hover:bg-white shadow-lg transition-all"
        >
          <ChevronRight className="w-8 h-8" />
        </Button>
      </div>

      {/* Pagination Dots */}
      <div className="flex justify-center gap-3 mt-12">
        {appScreens.map((_, index) => (
          <div 
            key={index}
            className={`h-2 rounded-full transition-all duration-300 ${
              index === selectedIndex ? 'w-8 bg-[#FF6B9D]' : 'w-2 bg-pink-200'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

export default FeaturesCarousel;
